import { useState, type ReactNode } from 'react';
import { css } from '@emotion/react';
import { ActionType, ObjectType } from '@frontend/api-phone-audits';
import { UsersQueries } from '@frontend/api-users';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import {
  CalendarIcon,
  Chip,
  DateRangeField,
  DropdownFilterChipField,
  PopoverDialog,
  SearchField,
  Button,
  useControlledField,
  useFormField,
  usePopoverDialog,
  useTooltip,
} from '@frontend/design-system';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';
import { ACTION_TYPE_OPTIONS, SETTING_TYPE_OPTIONS } from './phone-system-history-config';

export type AuditEventFilters = {
  search: string;
  objectTypes: ObjectType[];
  actions: ActionType[];
  userIds: string[];
  startDate: string | undefined;
  endDate: string | undefined;
};

export const defaultAuditEventFilters: AuditEventFilters = {
  search: '',
  objectTypes: [],
  actions: [],
  userIds: [],
  startDate: undefined,
  endDate: undefined,
};

type PhoneSystemHistoryFiltersProps = {
  filters: AuditEventFilters;
  onFiltersChange: (filters: AuditEventFilters) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  isCompact?: boolean;
};

export function PhoneSystemHistoryFilters({
  filters,
  onFiltersChange,
  searchValue,
  setSearchValue,
  isCompact = false,
}: PhoneSystemHistoryFiltersProps) {
  const { t } = useTranslation('phone');
  const tenantLocationIds = useTenantLocationIds();

  // ----- User list -----
  const { data: usersData, isLoading: usersIsLoading } = UsersQueries.useGetUsersInLocations(tenantLocationIds);
  const users = usersData?.users ?? [];

  // ----- Search field -----
  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchValue,
    onChange: (value) => setSearchValue(value as string),
  });

  // Shared reset key — incremented whenever any dropdown closes without applying,
  // so that all dropdowns reset their draft values to the current filters. This is to
  // avoid a situation where a user opens a dropdown, changes a draft value, closes without applying,
  // then opens it again and sees the old draft value instead of the current filter value.
  // Only one dropdown can be open at a time, so resetting all drafts together is safe.
  const [draftResetKey, setDraftResetKey] = useState(0);
  const bumpResetKey = () => setDraftResetKey((k) => k + 1);

  // ----- Date Range -----
  const today = formatDate(new Date().toISOString(), 'YYYY-MM-DD');
  const dateRangeDraftField = useFormField(
    { type: 'dateRange', value: [filters.startDate ?? '', filters.endDate ?? ''] },
    [draftResetKey, filters.startDate, filters.endDate]
  );
  // ----- Setting Type -----
  const objectTypesField = useFormField({ type: 'checklist', value: filters.objectTypes as string[] }, [
    draftResetKey,
    JSON.stringify(filters.objectTypes),
  ]);

  // ----- Action -----
  const actionTypesField = useFormField({ type: 'checklist', value: filters.actions as string[] }, [
    draftResetKey,
    JSON.stringify(filters.actions),
  ]);

  // ----- User -----
  const userIdsField = useFormField({ type: 'checklist', value: filters.userIds }, [
    draftResetKey,
    JSON.stringify(filters.userIds),
  ]);

  const anyFilterActive =
    filters.objectTypes.length > 0 ||
    filters.actions.length > 0 ||
    filters.userIds.length > 0 ||
    !!filters.startDate ||
    !!filters.endDate;

  const handleClearAll = () => {
    onFiltersChange(defaultAuditEventFilters);
    setSearchValue('');
  };

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--fabric-space-gap-sm);
        padding-bottom: var(--fabric-space-padding-sm);
        width: 100%;
      `}
    >
      {/* Date Range Filter */}
      <CompactFilterChip label={t('Date Range')} isCompact={isCompact}>
        <DateRangeChip
          filters={filters}
          onFiltersChange={onFiltersChange}
          dateRangeDraftField={dateRangeDraftField}
          today={today}
          onDismiss={bumpResetKey}
        />
      </CompactFilterChip>

      {/* Setting Type Filter */}
      <CompactFilterChip label={t('Setting Type')} isCompact={isCompact}>
        <DropdownFilterChipField
          {...objectTypesField}
          name='settingType'
          label={t('Setting Type')}
          icon='label'
          filterIsActive={filters.objectTypes.length > 0}
          showClearAll={false}
          onApply={({ value, close }) => {
            onFiltersChange({ ...filters, objectTypes: value as ObjectType[] });
            close();
          }}
          onDismiss={bumpResetKey}
        >
          {SETTING_TYPE_OPTIONS.map(({ value, label }) => (
            <DropdownFilterChipField.Option key={value} value={value}>
              {label}
            </DropdownFilterChipField.Option>
          ))}
        </DropdownFilterChipField>
      </CompactFilterChip>

      {/* User Filter */}
      <CompactFilterChip label={t('User')} isCompact={isCompact}>
        <DropdownFilterChipField
          {...userIdsField}
          name='user'
          label={t('User')}
          icon='user'
          filterIsActive={filters.userIds.length > 0}
          showSearchBar
          showClearAll={false}
          onApply={({ value, close }) => {
            onFiltersChange({ ...filters, userIds: value });
            close();
          }}
          onDismiss={bumpResetKey}
        >
          {usersIsLoading ? (
            <DropdownFilterChipField.Option value='' disabled>
              {t('Loading...')}
            </DropdownFilterChipField.Option>
          ) : (
            users?.map((user) => (
              <DropdownFilterChipField.Option key={user.userId} value={user.userId ?? ''}>
                {[user.firstName, user.lastName].filter(Boolean).join(' ')}
              </DropdownFilterChipField.Option>
            ))
          )}
        </DropdownFilterChipField>
      </CompactFilterChip>

      {/* Action Filter */}
      <CompactFilterChip label={t('Action')} isCompact={isCompact}>
        <DropdownFilterChipField
          {...actionTypesField}
          name='action'
          label={t('Action')}
          icon='action-bar'
          filterIsActive={filters.actions.length > 0}
          showClearAll={false}
          onApply={({ value, close }) => {
            onFiltersChange({ ...filters, actions: value as ActionType[] });
            close();
          }}
          onDismiss={bumpResetKey}
        >
          {ACTION_TYPE_OPTIONS.map(({ value, label }) => (
            <DropdownFilterChipField.Option key={value} value={value}>
              {label}
            </DropdownFilterChipField.Option>
          ))}
        </DropdownFilterChipField>
      </CompactFilterChip>

      {/* Global Clear All — single button, appears only when any filter is active */}
      {anyFilterActive && (
        <Button variant='tertiary' onClick={handleClearAll}>
          {t('Clear All')}
        </Button>
      )}

      {/* Spacer pushes search to the right */}
      <div
        css={css`
          flex: 1;
          min-width: var(--fabric-space-gap-sm);
        `}
      />

      {/* Search */}
      <SearchField
        {...searchFieldProps}
        name='search'
        label={t('Search Name or User')}
        placeholder={t('Search Name or User')}
        css={css`
          width: ${isCompact ? '200px' : '300px'};
          transition: width 0.3s ease;

          // css to truncate the label when the search field is compacted, so that it doesn't overflow past the right
          // edge of the input box.
          label {
            max-width: calc(100% - 40px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}
      />
    </div>
  );
}

// --- CompactFilterChip ---
// Wraps any dropdown filter chip component, animating its .chip-text to 0-width when isCompact is true so that the
// chip collapses to just the icon (hides the label) and displays a tooltip with the filter label in that state.
type CompactFilterChipProps = {
  label: string;
  isCompact: boolean;
  children: ReactNode;
};

function CompactFilterChip({ label, isCompact, children }: CompactFilterChipProps) {
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  return (
    <>
      <div
        {...(isCompact ? triggerProps : {})}
        css={css`
          display: inline-flex;
          .chip-text {
            display: inline-block;
            max-width: ${isCompact ? '0px' : '200px'};
            overflow: hidden;
            white-space: nowrap;
            transition: max-width 0.3s ease, opacity 0.3s ease;
            opacity: ${isCompact ? 0 : 1};
          }
        `}
      >
        {children}
      </div>
      <Tooltip {...tooltipProps}>{label}</Tooltip>
    </>
  );
}

// --- DateRangeChip ---
// Custom chip trigger + PopoverDialog because DropdownFilterChipField's floating container
// has a hardcoded maxWidth: 300px that cannot be overridden via floatingStyleOverrides.
type DateRangeChipProps = {
  filters: AuditEventFilters;
  onFiltersChange: (filters: AuditEventFilters) => void;
  dateRangeDraftField: ReturnType<typeof useFormField<'dateRange'>>;
  today: string;
  onDismiss: () => void;
};

function DateRangeChip({ filters, onFiltersChange, dateRangeDraftField, today, onDismiss }: DateRangeChipProps) {
  const { t } = useTranslation('phone');
  const isActive = !!(filters.startDate || filters.endDate);

  const { getTriggerProps, getDialogProps, close } = usePopoverDialog({
    placement: 'bottom-start',
    onClose: onDismiss,
  });

  const handleApply = () => {
    const [start, end] = dateRangeDraftField.value ?? ['', ''];
    onFiltersChange({
      ...filters,
      startDate: start || undefined,
      endDate: end || undefined,
    });
    close();
  };

  const handleClear = () => {
    dateRangeDraftField.onChange({ value: ['', ''], name: 'dateRange' });
  };

  return (
    <>
      <Chip.DropdownFilter
        {...getTriggerProps()}
        filterIsActive={isActive}
        leftElement={<CalendarIcon size={16} css={{ flexShrink: 0 }} />}
        css={{ maxWidth: 'none' }}
      >
        {t('Date Range')}
      </Chip.DropdownFilter>

      <PopoverDialog
        {...getDialogProps()}
        css={css`
          padding: var(--fabric-space-padding-md);
          display: flex;
          flex-direction: column;
          gap: var(--fabric-space-gap-sm);
        `}
      >
        <DateRangeField {...dateRangeDraftField} name='dateRange' label={t('Date Range')} maxDate={today} />
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: var(--fabric-space-gap-sm);
          `}
        >
          <Button size='small' variant='tertiary' onClick={handleClear}>
            {t('Clear')}
          </Button>
          <Button size='small' onClick={handleApply}>
            {t('Apply')}
          </Button>
        </div>
      </PopoverDialog>
    </>
  );
}
